import { Badge, Button, Card, Divider, Group, Stack, Text } from '@mantine/core';

import { UiNode } from '@repo/contracts/chatbot';

import classes from './UiTreeRenderer.module.css';

interface UiTreeRendererProps {
    node: UiNode;
    onAction?: (actionKey: string, label: string) => void;
}

function UiTextNode({ node }: { node: any }) {
    const textValue = node.value || node.label || '[Texte]';

    let size = 'sm';
    if (node.variant === 'title') size = 'md';
    
    let defaultColor = undefined;
    if (node.variant === 'subtitle') defaultColor = 'dimmed';
    if (node.variant === 'title') defaultColor = 'nli-nuit.9';
    if (!node.variant || node.variant === 'body') defaultColor = 'nli-nuit.7';

    if (node.color === 'primary') defaultColor = 'nli-liberty';
    if (node.color === 'secondary') defaultColor = 'nli-nuit.5';
    if (node.color === 'danger') defaultColor = 'red';
    if (node.color === 'success') defaultColor = 'teal';
    if (node.color === 'dimmed') defaultColor = 'dimmed';

    let fw = 400;
    if (node.variant === 'title') fw = 600;
    if (node.variant === 'subtitle') fw = 500;

    return (
        <Text
            m={0}
            size={size}
            fw={fw}
            c={node.color === 'default' ? undefined : defaultColor}
            lh={1.4}
        >
            {String(textValue)}
        </Text>
    );
}

function UiBadgeNode({ node }: { node: any }) {
    let color = 'nli-liberty';
    if (node.color === 'secondary') color = 'gray';
    if (node.color === 'danger') color = 'red';
    if (node.color === 'success') color = 'teal';
    if (node.color === 'info') color = 'blue';

    const badgeValue = node.value || node.label || '[Badge]';

    return (
        <Badge 
            variant="light" 
            color={color} 
            size="sm" 
            radius="sm" 
            style={{ textTransform: 'none', fontWeight: 600 }}
        >
            {String(badgeValue)}
        </Badge>
    );
}

function UiButtonNode({ node, onAction }: { node: any; onAction?: (actionKey: string, label: string) => void }) {
    const color = 'nli-liberty';

    const handleClick = () => {
        if (typeof node.action === 'string' && node.action.trim() !== '') {
            onAction?.(node.action, String(node.label || 'Action'));
        }
    };

    return (
        <Button
            variant="light"
            color={color}
            size="xs"
            radius="xl"
            mt="xs"
            fullWidth
            onClick={handleClick}
            style={{ transition: 'all 0.2s ease' }}
        >
            {String(node.label || 'Action')}
        </Button>
    );
}

function UiCardNode({ node, onAction }: { node: any; onAction?: (actionKey: string, label: string) => void }) {
    const isClickable = typeof node.action === 'string' && node.action.trim() !== '';
    
    return (
        <Card 
            shadow="sm" 
            p="md" 
            radius="md"
            withBorder
            className={isClickable ? classes.clickableCard : undefined}
            onClick={isClickable ? () => onAction?.(node.action, 'Card Action') : undefined}
            style={{ 
                borderColor: 'var(--mantine-color-gray-3)',
                backgroundColor: 'white',
            }}
        >
            <Stack gap="xs">
                {Array.isArray(node.children) ? node.children.map((child: any, i: number) => (
                    <UiTreeRenderer key={i} node={child} onAction={onAction} />
                )) : null}
            </Stack>
        </Card>
    );
}

function UiStackNode({ node, onAction }: { node: any; onAction?: (actionKey: string, label: string) => void }) {
    if (node.direction === 'row' || node.direction === 'wrap') {
        return (
            <Group 
                gap="xs" 
                align="center" 
                justify="flex-start"
                wrap={node.direction === 'wrap' ? 'wrap' : 'nowrap'}
            >
                {Array.isArray(node.children) ? node.children.map((child: any, i: number) => (
                    <UiTreeRenderer key={i} node={child} onAction={onAction} />
                )) : null}
            </Group>
        );
    }
    return (
        <Stack 
            gap="xs"
            align="stretch" 
            justify="flex-start"
        >
            {Array.isArray(node.children) ? node.children.map((child: any, i: number) => (
                <UiTreeRenderer key={i} node={child} onAction={onAction} />
            )) : null}
        </Stack>
    );
}

export function UiTreeRenderer({ node, onAction }: UiTreeRendererProps) {
    if (!node) return null;

    if (Array.isArray(node)) {
        return (
            <Stack gap="xs">
                {node.map((child, i) => (
                    <UiTreeRenderer key={i} node={child} onAction={onAction} />
                ))}
            </Stack>
        );
    }

    const safeNode = node as any;
    let nodeType = safeNode?.type;
    
    if (typeof nodeType !== 'string') return null;

    if (nodeType === 'list') nodeType = 'stack';
    if (nodeType === 'item') nodeType = 'card';
    if (nodeType === 'column' || nodeType === 'row') {
        safeNode.direction = nodeType;
        nodeType = 'stack';
    }

    switch (nodeType) {
        case 'divider':
            return <Divider my="xs" variant="dashed" />;
        case 'text':
            return <UiTextNode node={safeNode} />;
        case 'badge':
            return <UiBadgeNode node={safeNode} />;
        case 'button':
            return <UiButtonNode node={safeNode} onAction={onAction} />;
        case 'card':
            return <UiCardNode node={safeNode} onAction={onAction} />;
        case 'stack':
            return <UiStackNode node={safeNode} onAction={onAction} />;
        default:
            return null; // Silent fail rather than red text, better for UI immersion
    }
}
